import {
  createContext,
  useContext,
} from 'react';

import { theme as lightTheme } from '../../../assets/themes/theme';
import { dark as darkTheme } from '../../../assets/themes/dark';

/**
 * Theme consumption context for the Box/Flex/Text primitives.
 *
 * During the styled-components -> Tailwind migration, Box/Flex/Text
 * resolve their prop-to-inline-style API values using whatever theme
 * object is installed on this context. The test harness
 * (src/test/test-utils.jsx) provides a test theme that matches the
 * long-standing testTheme values referenced by hundreds of existing
 * assertions (e.g. `space[3] = '1rem'`); production renders use the
 * real theme (`theme.ts` or `dark.ts`).
 *
 * This is a strict shim. Box/Flex/Text are scheduled for removal once
 * feature-file callers are migrated to Tailwind className. New code
 * should NOT rely on this context.
 */
export interface BoxThemeLike {
  space: readonly (string | number)[] | Record<string | number, string | number>;
  fontSizes: readonly (string | number)[] | Record<string | number, string | number>;
  radii: readonly (string | number)[] | Record<string | number, string | number>;
  borders: readonly (string | number)[] | Record<string | number, string | number>;
  shadows: readonly (string | number)[] | Record<string | number, string | number>;
  colors: {
    [family: string]:
      | string
      | readonly string[]
      | Record<string | number, string>;
  };
}

// Default to the light theme. The Flex/Text/Box components wrap this
// lookup in `useBoxTheme()`. In production this context is never
// explicitly Provider'd, so the default wins.
export const BoxThemeContext = createContext<BoxThemeLike>(
  lightTheme as unknown as BoxThemeLike,
);

export const useBoxTheme = (): BoxThemeLike => useContext(BoxThemeContext);

// Convenience exports for callers that need the raw theme objects.
export {
  lightTheme,
  darkTheme,
};
